import { ENV } from './env';
import i18n, { UserError } from '../i18n';

/** Firebase Web SDK (only app + auth, B7). Loaded lazily so the tenant portal stays small. */
async function auth() {
  if (!ENV.firebase.apiKey) throw new UserError(i18n.t('auth.firebase.notConfigured'));
  const { initializeApp, getApps } = await import('firebase/app');
  const { getAuth } = await import('firebase/auth');
  const app = getApps()[0] ?? initializeApp(ENV.firebase);
  const a = getAuth(app);
  a.languageCode = i18n.language;
  return a;
}
export const firebaseConfigured = () => !!ENV.firebase.apiKey;

/** Sign-in needs the second factor (TOTP): Login shows the code step and calls resolveMfa. */
export class MfaRequired extends Error {
  constructor(readonly resolver: import('firebase/auth').MultiFactorResolver) {
    super('mfa-required');
  }
}
async function withMfa(fn: () => Promise<string>): Promise<string> {
  try {
    return await fn();
  } catch (e) {
    if ((e as { code?: string })?.code !== 'auth/multi-factor-auth-required') throw e;
    const { getMultiFactorResolver } = await import('firebase/auth');
    throw new MfaRequired(getMultiFactorResolver(await auth(), e as import('firebase/auth').MultiFactorError));
  }
}
/** Completes a sign-in with the 6-digit code of the authenticator app. */
export async function resolveMfa(resolver: import('firebase/auth').MultiFactorResolver, code: string) {
  const { TotpMultiFactorGenerator } = await import('firebase/auth');
  const hint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID) ?? resolver.hints[0];
  if (!hint) throw new UserError(i18n.t('auth.firebase.failed'));
  const c = await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code));
  return c.user.getIdToken();
}

export async function emailLogin(email: string, password: string) {
  const { signInWithEmailAndPassword } = await import('firebase/auth');
  return withMfa(async () => (await signInWithEmailAndPassword(await auth(), email, password)).user.getIdToken());
}
export async function emailSignup(email: string, password: string, name: string) {
  const { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } = await import('firebase/auth');
  const c = await createUserWithEmailAndPassword(await auth(), email, password);
  await updateProfile(c.user, { displayName: name });
  await sendEmailVerification(c.user);
  return c.user.getIdToken(true);
}
export async function providerLogin(kind: 'google' | 'apple') {
  const { signInWithPopup, GoogleAuthProvider, OAuthProvider } = await import('firebase/auth');
  const provider = kind === 'google' ? new GoogleAuthProvider() : new OAuthProvider('apple.com');
  if (kind === 'apple') (provider as InstanceType<typeof OAuthProvider>).addScope('email');
  return withMfa(async () => (await signInWithPopup(await auth(), provider)).user.getIdToken());
}
export async function resetPassword(email: string) {
  const { sendPasswordResetEmail } = await import('firebase/auth');
  await sendPasswordResetEmail(await auth(), email);
}
/** Firebase user of this browser (null after a dev login or when the Firebase session is gone). */
export async function firebaseUser() {
  if (!firebaseConfigured()) return null;
  const a = await auth();
  await a.authStateReady();
  return a.currentUser;
}
export type TotpSetup = { secretKey: string; uri: string; finish: (code: string) => Promise<void> };
/** Starts TOTP enrolment (design/Sicherheit "2-Faktor einrichten"); `finish` verifies the first code and enrols. */
export async function startTotp(issuer: string): Promise<TotpSetup> {
  const { multiFactor, TotpMultiFactorGenerator } = await import('firebase/auth');
  const user = await firebaseUser();
  if (!user) throw Object.assign(new Error('no firebase user'), { code: 'auth/requires-recent-login' });
  const secret = await TotpMultiFactorGenerator.generateSecret(await multiFactor(user).getSession());
  return {
    secretKey: secret.secretKey,
    uri: secret.generateQrCodeUrl(user.email ?? issuer, issuer),
    finish: async (code) => multiFactor(user).enroll(TotpMultiFactorGenerator.assertionForEnrollment(secret, code), issuer),
  };
}
/** Enrolled second factors of the current user. */
export async function secondFactors() {
  const user = await firebaseUser();
  if (!user) return null;
  const { multiFactor } = await import('firebase/auth');
  return multiFactor(user).enrolledFactors.map((f) => ({ uid: f.uid, name: f.displayName ?? f.factorId, factorId: f.factorId, enrolledAt: f.enrollmentTime }));
}
export async function removeSecondFactor(uid: string) {
  const user = await firebaseUser();
  if (!user) throw Object.assign(new Error('no firebase user'), { code: 'auth/requires-recent-login' });
  const { multiFactor } = await import('firebase/auth');
  await multiFactor(user).unenroll(uid);
}
export async function firebaseSignOut() {
  if (!firebaseConfigured()) return;
  const { signOut } = await import('firebase/auth');
  await signOut(await auth()).catch(() => undefined);
}
const FIREBASE_ERRORS: Record<string, string> = {
  'auth/invalid-credential': 'invalidCredential',
  'auth/user-not-found': 'invalidCredential',
  'auth/wrong-password': 'invalidCredential',
  'auth/email-already-in-use': 'emailInUse',
  'auth/weak-password': 'weakPassword',
  'auth/too-many-requests': 'tooManyRequests',
  'auth/popup-closed-by-user': 'popupClosed',
  'auth/multi-factor-auth-required': 'mfaRequired',
  'auth/invalid-verification-code': 'invalidCode',
  'auth/requires-recent-login': 'recentLogin',
  'auth/operation-not-allowed': 'mfaUnavailable',
  'auth/unsupported-first-factor': 'mfaUnsupported',
  'auth/missing-email': 'missingEmail',
  'auth/invalid-email': 'invalidEmail',
};
export function firebaseErrorText(e: unknown): string {
  const code = (e as { code?: string })?.code ?? '';
  const key = FIREBASE_ERRORS[code];
  if (key) return i18n.t(`auth.firebase.${key}`);
  if (code === 'auth/network-request-failed') return i18n.t('auth.firebase.network');
  // never show Firebase's own English texts ("Firebase: Error (auth/…)") to the user
  const message = (e as Error)?.message ?? '';
  return code.startsWith('auth/') || !message || message.startsWith('Firebase') ? i18n.t('auth.firebase.unknown') : message;
}
